import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';
import {
  Users, Crown, Check, X, Clock, Search, Shield, ChevronDown, Loader
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  none: { text: 'Ücretsiz', color: '#94A3B8', bg: 'rgba(148,163,184,0.1)' },
  pending: { text: 'Onay Bekliyor', color: '#FACC15', bg: 'rgba(250,204,21,0.1)' },
  active: { text: 'Premium', color: '#34D399', bg: 'rgba(34,197,94,0.1)' },
  expired: { text: 'Süresi Dolmuş', color: '#F87171', bg: 'rgba(248,113,113,0.1)' },
};

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND}/api/admin/users`, { withCredentials: true });
      setUsers(res.data);
    } catch { }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/dashboard'); return; }
    fetchUsers();
  }, [user, navigate, fetchUsers]);

  const handleAction = async (userId, action, days = 30) => {
    setActionLoading(userId);
    try {
      await axios.put(`${BACKEND}/api/admin/users/${userId}/premium`, {
        action, duration_days: days,
      }, { withCredentials: true });
      await fetchUsers();
    } catch { }
    setActionLoading(null);
  };

  const filtered = users.filter(u => {
    if (filter === 'pending' && u.premium_status !== 'pending') return false;
    if (filter === 'active' && u.premium_status !== 'active') return false;
    if (filter === 'free' && u.premium_status !== 'none') return false;
    if (search) {
      const s = search.toLowerCase();
      return (u.email || '').toLowerCase().includes(s) || (u.name || '').toLowerCase().includes(s);
    }
    return true;
  });

  const pendingCount = users.filter(u => u.premium_status === 'pending').length;

  return (
    <div className="min-h-screen" style={{ background: 'transparent' }}>
      <PageSEO title="Admin Panel" />
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.2)' }}>
              <Shield className="w-5 h-5" style={{ color: '#A78BFA' }} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
              <p className="text-sm" style={{ color: '#64748B' }}>{users.length} kullanıcı</p>
            </div>
          </div>
          {pendingCount > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: 'rgba(250,204,21,0.1)', border: '1px solid rgba(250,204,21,0.2)' }}>
              <Clock className="w-4 h-4" style={{ color: '#FACC15' }} />
              <span className="text-sm font-semibold" style={{ color: '#FACC15' }}>{pendingCount} onay bekliyor</span>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#64748B' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="E-posta veya isim ara..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm outline-none"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: '#E2E8F0' }}
              data-testid="admin-search" />
          </div>
          {['all', 'pending', 'active', 'free'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className="px-4 py-2 rounded-xl text-xs font-semibold transition-all"
              style={{
                background: filter === f ? 'rgba(34,211,238,0.1)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${filter === f ? 'rgba(34,211,238,0.3)' : 'rgba(255,255,255,0.06)'}`,
                color: filter === f ? '#67E8F9' : '#94A3B8',
              }}
              data-testid={`admin-filter-${f}`}>
              {f === 'all' ? 'Tümü' : f === 'pending' ? `Bekleyen (${pendingCount})` : f === 'active' ? 'Premium' : 'Ücretsiz'}
            </button>
          ))}
        </div>

        {/* User List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader className="w-6 h-6 animate-spin" style={{ color: '#67E8F9' }} />
          </div>
        ) : (
          <div className="space-y-2" data-testid="admin-user-list">
            {filtered.map(u => {
              const status = STATUS_LABELS[u.premium_status] || STATUS_LABELS.none;
              const isProcessing = actionLoading === u.id;
              return (
                <div key={u.id} className="flex items-center gap-4 px-5 py-4 rounded-xl transition-all"
                  style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
                  data-testid={`admin-user-${u.id}`}>
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0" style={{
                    background: u.premium_status === 'active' ? 'linear-gradient(135deg, #FACC15, #F59E0B)' : 'rgba(255,255,255,0.06)',
                    color: u.premium_status === 'active' ? '#000' : '#94A3B8',
                  }}>
                    {(u.name || 'U').charAt(0).toLocaleUpperCase('tr-TR')}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{u.name} {u.surname}</p>
                    <p className="text-xs truncate" style={{ color: '#64748B' }}>{u.email}</p>
                  </div>

                  {/* Status badge */}
                  <span className="px-3 py-1 rounded-full text-xs font-semibold shrink-0" style={{ background: status.bg, color: status.color }}>
                    {status.text}
                  </span>

                  {/* Actions */}
                  <div className="flex gap-2 shrink-0">
                    {u.premium_status === 'pending' && (
                      <button onClick={() => handleAction(u.id, 'activate', 30)} disabled={isProcessing}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-black transition-all hover:scale-105 disabled:opacity-50"
                        style={{ background: 'linear-gradient(135deg, #34D399, #10B981)' }}
                        data-testid={`admin-activate-${u.id}`}>
                        {isProcessing ? <Loader className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                        Onayla (30 gün)
                      </button>
                    )}
                    {u.premium_status === 'active' && (
                      <button onClick={() => handleAction(u.id, 'revoke')} disabled={isProcessing}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all hover:bg-red-500/20 disabled:opacity-50"
                        style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#FCA5A5' }}
                        data-testid={`admin-revoke-${u.id}`}>
                        <X className="w-3 h-3" /> İptal
                      </button>
                    )}
                    {(u.premium_status === 'none' || u.premium_status === 'expired') && u.role !== 'admin' && (
                      <button onClick={() => handleAction(u.id, 'activate', 30)} disabled={isProcessing}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all disabled:opacity-50"
                        style={{ background: 'rgba(250,204,21,0.1)', border: '1px solid rgba(250,204,21,0.2)', color: '#FACC15' }}
                        data-testid={`admin-grant-${u.id}`}>
                        <Crown className="w-3 h-3" /> Premium Ver
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <p className="text-center py-12" style={{ color: '#64748B' }}>Kullanıcı bulunamadı</p>
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
